import { useState, useCallback, useEffect, useRef } from 'react';

const PROPERTY_API_URL = 'https://property-lookup-api.anassk.workers.dev';
const PROPERTY_API_KEY = '19285196e984c67c9c96784becbd3464e692815a4393e4c948be460a2019182e';

// Cache storm data for 1 hour
const STORM_CACHE_KEY = 'leadgen_storm_cache';
const STORM_CACHE_TTL = 60 * 60 * 1000; // 1 hour

function getStormCache() {
  try {
    const cached = localStorage.getItem(STORM_CACHE_KEY);
    if (!cached) return null;
    const data = JSON.parse(cached);
    if (Date.now() - data.timestamp > STORM_CACHE_TTL) {
      localStorage.removeItem(STORM_CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setStormCache(storms, bounds) {
  try {
    localStorage.setItem(STORM_CACHE_KEY, JSON.stringify({
      storms,
      bounds,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.warn('Failed to cache storm data:', e);
  }
}

// Calculate distance between two points in km (Haversine formula)
function getDistanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

// Calculate storm score for a property
// Higher score = more likely to need roof work
function calculateStormScore(storms, lat, lng, assessedValue = 0) {
  if (!storms || storms.length === 0) return 0;

  let score = 0;
  const now = Date.now();

  storms.forEach(storm => {
    const distanceKm = getDistanceKm(lat, lng, storm.latitude, storm.longitude);

    // Only consider storms within 15km radius
    if (distanceKm > 15) return;

    // Recency factor (1.0 for today, 0.5 for 30 days ago, 0.1 for 90 days)
    const stormDate = new Date(storm.event_date).getTime();
    const daysSince = (now - stormDate) / (1000 * 60 * 60 * 24);
    const recencyFactor = Math.max(0.1, 1 - (daysSince / 100));

    // Magnitude factor based on event type
    let magnitudeFactor = 1;
    if (storm.event_type === 'hail') {
      // Hail size in inches: 1" = moderate, 2"+ = severe
      magnitudeFactor = storm.magnitude ? Math.min(3, storm.magnitude / 0.75) : 1;
    } else if (storm.event_type === 'wind') {
      // Wind speed in mph: 60mph = moderate, 80mph+ = severe
      magnitudeFactor = storm.magnitude ? Math.min(3, storm.magnitude / 50) : 1;
    } else if (storm.event_type === 'tornado') {
      // All tornados are severe
      magnitudeFactor = 3;
    }

    // Distance factor (closer = higher impact)
    const distanceFactor = Math.max(0.2, 1 - (distanceKm / 15));

    // Property value factor (higher value = more premium roof likely)
    const valueFactor = assessedValue > 300000 ? 1.3 : assessedValue > 150000 ? 1.1 : 1;

    score += recencyFactor * magnitudeFactor * distanceFactor * valueFactor * 100;
  });

  return Math.round(Math.min(score, 1000)); // Cap at 1000
}

export function useStormData() {
  const [storms, setStorms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSyncDate, setLastSyncDate] = useState(null);
  const boundsRef = useRef(null);
  const initialFetchDone = useRef(false);

  // Initial fetch on mount - broad Indiana/Midwest area
  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;

    // Check cache first
    const cached = getStormCache();
    if (cached && cached.storms.length > 0) {
      console.log('⚡ Loaded', cached.storms.length, 'storms from cache');
      setStorms(cached.storms);
      return;
    }

    // Fetch for broad area (Indiana + surrounding states)
    const defaultBounds = {
      north: 42,  // Northern Indiana border
      south: 38,  // Southern Indiana border
      east: -84,  // Eastern Indiana
      west: -88   // Western Indiana
    };

    console.log('⚡ Fetching initial storm data...');
    fetch(
      `${PROPERTY_API_URL}/storms?minLat=${defaultBounds.south}&maxLat=${defaultBounds.north}&minLng=${defaultBounds.west}&maxLng=${defaultBounds.east}&days=90`,
      { headers: { 'X-API-Key': PROPERTY_API_KEY } }
    )
      .then(res => res.json())
      .then(data => {
        if (data.success && data.storms) {
          console.log('⚡ Loaded', data.storms.length, 'storms from API');
          setStorms(data.storms);
          setLastSyncDate(data.lastSync);
          setStormCache(data.storms, defaultBounds);
        }
      })
      .catch(e => console.error('Initial storm fetch failed:', e));
  }, []);

  // Fetch storms for current map viewport
  const fetchStorms = useCallback(async (bounds, days = 90) => {
    if (!bounds) return;

    const { north, south, east, west } = bounds;

    // Check if bounds changed significantly
    if (boundsRef.current) {
      const prev = boundsRef.current;
      const latDiff = Math.abs(north - prev.north) + Math.abs(south - prev.south);
      const lngDiff = Math.abs(east - prev.east) + Math.abs(west - prev.west);
      if (latDiff < 0.01 && lngDiff < 0.01) return; // Bounds barely changed
    }
    boundsRef.current = bounds;

    // Check local cache first
    const cached = getStormCache();
    if (cached && cached.storms.length > 0) {
      // Check if cached bounds contain current bounds
      const cb = cached.bounds;
      if (cb && north <= cb.north && south >= cb.south && east <= cb.east && west >= cb.west) {
        setStorms(cached.storms);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${PROPERTY_API_URL}/storms?minLat=${south}&maxLat=${north}&minLng=${west}&maxLng=${east}&days=${days}`,
        { headers: { 'X-API-Key': PROPERTY_API_KEY } }
      );

      const data = await response.json();

      if (data.success && data.storms) {
        setStorms(data.storms);
        setLastSyncDate(data.lastSync);
        setStormCache(data.storms, bounds);
      } else if (data.error) {
        setError(data.error);
      }
    } catch (e) {
      console.error('Failed to fetch storms:', e);
      setError(e.message);
    }

    setLoading(false);
  }, []);

  // Check if a specific property is storm-affected
  const checkProperty = useCallback((lat, lng, radiusKm = 5) => {
    if (!storms || storms.length === 0) return { affected: false, storms: [], score: 0 };

    const nearbyStorms = storms.filter(storm => {
      const distance = getDistanceKm(lat, lng, storm.latitude, storm.longitude);
      return distance <= radiusKm;
    });

    return {
      affected: nearbyStorms.length > 0,
      storms: nearbyStorms,
      score: calculateStormScore(nearbyStorms, lat, lng),
      nearestStorm: nearbyStorms.length > 0 ? nearbyStorms.reduce((nearest, storm) => {
        const d1 = getDistanceKm(lat, lng, nearest.latitude, nearest.longitude);
        const d2 = getDistanceKm(lat, lng, storm.latitude, storm.longitude);
        return d2 < d1 ? storm : nearest;
      }) : null
    };
  }, [storms]);

  // Sync storm data from NOAA/SPC (admin only)
  const syncStorms = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${PROPERTY_API_URL}/storms/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': PROPERTY_API_KEY
        }
      });

      const data = await response.json();

      if (data.success) {
        setLastSyncDate(new Date().toISOString());
        // Clear cache to force reload
        localStorage.removeItem(STORM_CACHE_KEY);
        return data;
      } else {
        setError(data.error);
        return null;
      }
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get storm color based on type and magnitude
  const getStormColor = useCallback((storm) => {
    if (storm.event_type === 'tornado') return { fill: '#9333ea', stroke: '#7c3aed' }; // Purple
    if (storm.event_type === 'hail') {
      const size = storm.magnitude || 1;
      if (size >= 2) return { fill: '#dc2626', stroke: '#b91c1c' }; // Red - severe
      if (size >= 1) return { fill: '#f97316', stroke: '#ea580c' }; // Orange - significant
      return { fill: '#fbbf24', stroke: '#f59e0b' }; // Yellow - minor
    }
    if (storm.event_type === 'wind') {
      const speed = storm.magnitude || 60;
      if (speed >= 80) return { fill: '#dc2626', stroke: '#b91c1c' }; // Red
      return { fill: '#f97316', stroke: '#ea580c' }; // Orange
    }
    return { fill: '#6b7280', stroke: '#4b5563' }; // Gray default
  }, []);

  // Get storm radius in meters based on type and magnitude
  const getStormRadius = useCallback((storm) => {
    // Base radius 5km, scaled by magnitude
    let baseRadius = 5000; // 5km in meters

    if (storm.event_type === 'hail') {
      const size = storm.magnitude || 1;
      // Larger hail = larger damage radius (5-15km)
      baseRadius = 5000 + (size * 5000);
    } else if (storm.event_type === 'tornado') {
      // Tornados have smaller but intense damage radius (2-8km)
      baseRadius = 3000;
    } else if (storm.event_type === 'wind') {
      // Wind damage can be widespread (8-15km)
      baseRadius = 8000;
    }

    return Math.min(baseRadius, 15000); // Cap at 15km
  }, []);

  return {
    storms,
    loading,
    error,
    lastSyncDate,
    fetchStorms,
    checkProperty,
    syncStorms,
    getStormColor,
    getStormRadius,
    calculateStormScore: (lat, lng, assessedValue) => calculateStormScore(storms, lat, lng, assessedValue)
  };
}

export default useStormData;
